import CardPeek from "@/components/CardFace/CardPeek"
import CardThumb from "@/components/CardFace/CardThumb"
import { cardFace } from "@/content/cards"
import { CARDS_COPY } from "@/content/cardsRound"
import type { CardId } from "@/types"
import styles from "@/styles/pages/Cards.module.css"

/*
 * Your hand, as a row of slots — or, beside the deal, a column of them.
 *
 * Slots rather than a list, because the cap is the interesting thing: five empty
 * outlines tell someone how many more they get without a sentence, and the
 * pressure to fill them is what makes the round finish. With `handCap: 0` there
 * is no cap, so there is nothing to outline and it becomes a plain row.
 *
 * A card is dropped from the trash badge in its corner, not by clicking the tile
 * itself: the tile was a button whose only action was to discard, sitting next
 * to the eye people move onto it to read the card with, so the gesture for
 * looking and the gesture for losing it were a couple of pixels apart. Dropping
 * is the only way back out of a full hand, so it is never disabled — except
 * while the round is being read back, where the hand is a record of what was
 * chosen and there is nothing to get out of.
 */

interface HandSlotsProps {
  picked: readonly CardId[]
  cap: number
  onDrop: (cardId: CardId) => void
  readOnly?: boolean
  /** "aside" stands the slots in a column beside the deal, rather than in a
   *  single centred line — see the deal layout in `CardsPick`. */
  layout?: "row" | "aside"
}

export default function HandSlots({
  picked,
  cap,
  onDrop,
  readOnly = false,
  layout = "row",
}: HandSlotsProps) {
  const empties = cap > 0 ? Math.max(cap - picked.length, 0) : 0
  const aside = layout === "aside"

  return (
    <div className={aside ? `${styles.hand} ${styles.handAside}` : styles.hand}>
      <span className={aside ? `${styles.handLabel} ${styles.handLabelAside}` : styles.handLabel}>
        {CARDS_COPY.handLabel}
        <span className={styles.handCount}>{CARDS_COPY.handCount(picked.length, cap)}</span>
      </span>

      <div className={aside ? `${styles.slots} ${styles.slotsGrid}` : styles.slots}>
        {picked.map((cardId) => (
          <CardPeek
            key={cardId}
            cardId={cardId}
            className={aside ? styles.handSlotCard : undefined}
            onRemove={readOnly ? undefined : () => onDrop(cardId)}
            removeLabel={CARDS_COPY.dropFromHand(cardFace(cardId)?.name ?? cardId)}
          >
            {/* No `onClick`: the thumb is a span here, so the only thing in the
                slot that can be pressed is a badge that says what it does. */}
            <CardThumb
              cardId={cardId}
              size="sm"
              withStatement={aside}
              className={aside ? styles.handThumb : undefined}
            />
          </CardPeek>
        ))}

        {Array.from({ length: empties }, (_, index) => (
          <span key={`empty-${index}`} className={styles.slot} aria-hidden="true">
            {picked.length + index + 1}
          </span>
        ))}

        {cap === 0 && picked.length === 0 ? (
          <span className={styles.slot} aria-hidden="true">
            {CARDS_COPY.handUnlimited}
          </span>
        ) : null}
      </div>
    </div>
  )
}
